import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { clearPricingSignupDraft } from '../utils/pricingSignupDraft';
import { friendlyOrderNumber } from '../utils/friendlyOrderNumber';
import { suppressExitPopupAfterPayment } from '../components/PrairiSignalsExitPopup';

/** Figma Investor LP — Welcome / payment confirmed modal */
const GREEN = '#6F9B41';
const GREEN_HOVER = '#5d8435';
const PANEL_BG = '#1a1a1a';

const CheckoutSuccess: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [confirmationNumber, setConfirmationNumber] = useState<string | null>(() =>
    sessionId ? friendlyOrderNumber(sessionId) : null
  );
  const [isResolvingSession, setIsResolvingSession] = useState(false);
  const [sessionResolveError, setSessionResolveError] = useState<string | null>(null);
  const storedUserIdRef = useRef<string | null>(null);
  const pendingUserTypeRef = useRef<'investor' | 'startup' | null>(null);

  useEffect(() => {
    suppressExitPopupAfterPayment();
    clearPricingSignupDraft();
    const storedUserId = localStorage.getItem('pendingUserId');
    const storedUserType = localStorage.getItem('pendingUserType');
    if (storedUserType === 'investor' || storedUserType === 'startup') {
      pendingUserTypeRef.current = storedUserType;
    }
    if (storedUserId) {
      storedUserIdRef.current = storedUserId;
      localStorage.removeItem('pendingUserId');
      localStorage.removeItem('pendingUserType');
      setPendingUserId(storedUserId);
    }
  }, []);

  useEffect(() => {
    if (!sessionId) return;
    // Show PR-######## immediately — never flash Stripe's cs_… session id.
    setConfirmationNumber(friendlyOrderNumber(sessionId));

    let isMounted = true;
    const resolveSessionUser = async () => {
      setIsResolvingSession(true);
      setSessionResolveError(null);
      try {
        const resolveUserFn = httpsCallable<
          { sessionId: string },
          {
            success: boolean;
            userId?: string;
            confirmationNumber?: string;
            error?: string;
          }
        >(functions, 'getPendingUserBySession');

        const response = await resolveUserFn({ sessionId });
        if (!isMounted) return;

        if (response.data?.success && response.data.userId) {
          setPendingUserId(response.data.userId);
          if (response.data.confirmationNumber) {
            setConfirmationNumber(response.data.confirmationNumber);
          }
        } else if (!storedUserIdRef.current) {
          throw new Error(response.data?.error || 'Unable to resolve session.');
        }
      } catch (error) {
        if (!isMounted) return;
        if (storedUserIdRef.current) return;
        const message =
          error instanceof Error ? error.message : 'Unable to verify your payment session.';
        setSessionResolveError(message);
      } finally {
        if (isMounted) setIsResolvingSession(false);
      }
    };

    resolveSessionUser();
    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  const continueToVerifyEmail = () => {
    if (!pendingUserId) return;
    // Must click the verify-email link before creating a password / opening the profile form.
    navigate(`/verify-email?userId=${encodeURIComponent(pendingUserId)}`, { replace: true });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-dark-950 px-4 py-10">
      <div
        className="w-full max-w-[420px] rounded-[28px] border px-6 py-8 text-center text-white shadow-2xl sm:px-10 sm:py-10"
        style={{ backgroundColor: PANEL_BG, borderColor: GREEN, borderWidth: 1 }}
        role="dialog"
        aria-labelledby="checkout-success-title"
      >
        {/* Success check */}
        <div
          className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full border-2 sm:h-16 sm:w-16"
          style={{ borderColor: GREEN }}
          aria-hidden
        >
          <svg className="h-7 w-7 sm:h-8 sm:w-8" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 13l4 4L19 7"
              stroke={GREEN}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <h1
          id="checkout-success-title"
          className="mb-6 text-2xl font-bold tracking-tight sm:text-[28px]"
        >
          Welcome to PRAIRI
        </h1>

        {/* Payment confirmation number */}
        <div
          className="mb-3 rounded-xl border px-4 py-4"
          style={{ borderColor: GREEN, borderWidth: 1 }}
        >
          <p className="text-sm text-white/90 sm:text-base">Payment confirmation number:</p>
          <p className="mt-1 break-all text-lg font-bold leading-snug sm:text-xl">
            {confirmationNumber || '…'}
          </p>
        </div>

        <p className="mb-8 text-sm text-white/90 sm:text-[15px]">
          A confirmation email has been sent to your email.
        </p>

        <p className="mb-5 text-base font-semibold leading-snug sm:text-lg">
          Verify your email
          <br />
          to finish creating your PRAIRI account
        </p>

        {pendingUserId ? (
          <button
            type="button"
            onClick={continueToVerifyEmail}
            className="mb-6 w-full rounded-xl px-5 py-3 text-base font-semibold text-white transition-colors sm:text-lg"
            style={{ backgroundColor: GREEN }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN_HOVER;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLButtonElement).style.backgroundColor = GREEN;
            }}
          >
            Verify your email
          </button>
        ) : (
          <div className="mb-6 space-y-4 text-left">
            {isResolvingSession ? (
              <div className="flex items-center justify-center gap-3 rounded-xl border border-slate-700/60 bg-black/40 px-4 py-3 text-sm text-gray-200">
                <span className="flex h-3 w-3">
                  <span className="h-3 w-3 animate-ping rounded-full bg-slate-400 opacity-75" />
                  <span className="relative -ml-3 h-3 w-3 rounded-full bg-slate-200" />
                </span>
                Verifying your payment session…
              </div>
            ) : (
              <p className="text-center text-sm text-gray-300">
                We couldn&apos;t locate your pending account automatically. Use the button below to
                restart the onboarding flow.
              </p>
            )}

            {sessionResolveError && (
              <div className="rounded-xl border border-amber-400/30 bg-amber-500/10 px-4 py-3 text-center text-sm text-amber-200">
                {sessionResolveError}
              </div>
            )}

            {!isResolvingSession && (
              <button
                type="button"
                onClick={() =>
                  navigate(
                    pendingUserTypeRef.current === 'startup'
                      ? '/startups/pricing'
                      : '/investors/pricing'
                  )
                }
                className="w-full rounded-xl px-5 py-3 text-base font-semibold text-white transition-colors"
                style={{ backgroundColor: GREEN }}
              >
                Go back to Sign Up
              </button>
            )}
          </div>
        )}

        <p className="text-sm text-white/80">
          Need help?{' '}
          <button
            type="button"
            onClick={() => navigate('/contact')}
            className="font-medium underline decoration-dotted underline-offset-4 transition hover:opacity-90"
            style={{ color: GREEN }}
          >
            Contact our team.
          </button>
        </p>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
